import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { CategoryProduct } from "@/lib/catalog";

interface UseActiveProductsResult {
  products: CategoryProduct[];
  featured: CategoryProduct[];
  counts: Record<string, number>;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Carga únicamente los anuncios publicados y activos del Backend.
 * - `products`: los más recientes.
 * - `featured`: los marcados como destacados desde el panel de admin.
 * - `counts`: conteo real de anuncios por categoría.
 * No usa datos de prueba: si no hay publicaciones, devuelve listas vacías.
 */
export default function useActiveProducts(limit = 24): UseActiveProductsResult {
  const [products, setProducts] = useState<CategoryProduct[]>([]);
  const [featured, setFeatured] = useState<CategoryProduct[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, featuredRes, countRes] = await Promise.all([
        supabase
          .from("products")
          .select("*, profiles(full_name)")
          .eq("status", "active")
          .order("created_at", { ascending: false })
          .limit(limit),
        supabase
          .from("products")
          .select("*, profiles(full_name)")
          .eq("status", "active")
          .eq("featured", true)
          .order("created_at", { ascending: false }),
        supabase.from("products").select("category").eq("status", "active"),
      ]);

      if (listRes.error) throw listRes.error;
      if (featuredRes.error) throw featuredRes.error;
      if (countRes.error) throw countRes.error;

      setProducts((listRes.data ?? []) as unknown as CategoryProduct[]);
      setFeatured((featuredRes.data ?? []) as unknown as CategoryProduct[]);

      const next: Record<string, number> = {};
      (countRes.data ?? []).forEach((row: { category: string | null }) => {
        const key = row.category ?? "Sin categoría";
        next[key] = (next[key] ?? 0) + 1;
      });
      setCounts(next);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se pudieron cargar los anuncios publicados.",
      );
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { products, featured, counts, loading, error, refresh: fetchAll };
}